export interface StartLevelInput {
  width?: number;
  height?: number;
}

/**
 * Pick a start level that lands at the resolution the player will end up at,
 * to avoid the visible ABR climb from the bottom of the ladder.
 *
 * Two policies, depending on whether `level-cap` is active:
 *
 * - `levelCap=true`: mirror hls.js `capLevelToPlayerSize`, which caps at the
 *   smallest rung whose width AND height are >= the player size (in CSS px,
 *   because the cap is configured with `ignoreDevicePixelRatio=true`). We
 *   start at that same rung so the start level matches the cap exactly.
 *   Falls back to the highest available rung when every rung is smaller than
 *   the player (cap doesn't restrict in that case).
 *
 * - `levelCap=false`: there is no upper bound, but downloading a rung above
 *   what the display can show is wasted bandwidth, so pick the highest rung
 *   that fits within the DPR-adjusted player size. Returns -1 when no rung
 *   fits (player smaller than the smallest rung), letting hls.js's default
 *   ABR pick the start level.
 *
 * Returns -1 when the player size is unknown (not yet laid out), so the
 * caller can fall back to the default ABR start level.
 */
export function pickStartLevelForPlayerSize(
  levels: StartLevelInput[],
  playerWidth: number,
  playerHeight: number,
  levelCap: boolean,
  devicePixelRatio: number,
): number {
  if (levels.length === 0) return -1;
  if (playerWidth < 1 || playerHeight < 1) return -1;

  const dpr = levelCap ? 1 : devicePixelRatio;
  const targetWidth = Math.round(playerWidth * dpr);
  const targetHeight = Math.round(playerHeight * dpr);

  if (levelCap) {
    let capIndex = -1;
    let capHeight = Infinity;
    let highestIndex = -1;
    let highestHeight = 0;

    for (let i = 0; i < levels.length; i++) {
      const w = levels[i].width ?? 0;
      const h = levels[i].height ?? 0;
      if (w === 0 && h === 0) continue;

      if (h > highestHeight) {
        highestHeight = h;
        highestIndex = i;
      }

      const widthOk = w === 0 || w >= targetWidth;
      const heightOk = h === 0 || h >= targetHeight;
      if (widthOk && heightOk && h < capHeight) {
        capHeight = h;
        capIndex = i;
      }
    }

    return capIndex >= 0 ? capIndex : highestIndex;
  }

  let bestIndex = -1;
  let bestHeight = 0;
  for (let i = 0; i < levels.length; i++) {
    const w = levels[i].width ?? 0;
    const h = levels[i].height ?? 0;
    if (w === 0 && h === 0) continue;

    if (
      (w === 0 || w <= targetWidth) &&
      (h === 0 || h <= targetHeight) &&
      h > bestHeight
    ) {
      bestHeight = h;
      bestIndex = i;
    }
  }
  return bestIndex;
}
